import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { cn } from '@/lib/utils.js';
import { getDealReopen, reopenDeal } from '@/services/deals.service.js';

// Reopen an ended deal (deals-door-1 answers round, David 2026-10-28, Q1): an EDGE-CASE tool, deliberately quiet. A client
// who comes back is a new deal, so this is never the default path: no primary action, no pipeline move, no actions sheet
// offers it. It renders only when the server says this sign-in may reopen (its own Google sign-in holding the "Reopen an
// ended deal" privilege), the deal has ended, and the word before it ended is known; the page derives none of that. The
// server is the gate (requirePersonalPrivilege); the reason is required and goes on the activity line.
const REASON_MAX = 500;

export default function DealReopenKey({ dealId, onReopened }) {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [chosen, setChosen] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!dealId) return undefined;
    let alive = true;
    setState(null);
    getDealReopen(dealId).then((d) => { if (alive) setState(d || null); }).catch(() => { if (alive) setState(null); });
    return () => { alive = false; };
  }, [dealId]);
  useEffect(() => { if (open) { setReason(''); setChosen(''); } }, [open]);

  // QA-1 (round 2): when TRACON does not know the stage before it ended, the server lets the OWNER choose one (may_choose,
  // with the words it allows in choices). The page offers the picker only then and spells no stage word itself.
  const choosing = !!(state && state.may_choose && !(state.prior && state.prior.word) && Array.isArray(state.choices) && state.choices.length);
  if (!state || !state.ended || !state.may_reopen || (!(state.prior && state.prior.word) && !choosing)) return null;
  // A Cancelled deal is reinstated, not reopened: nothing to offer until the server says the reinstatement is on file.
  if (state.reinstatement && state.reinstatement.required && !state.reinstatement.on_file) return null;
  const prior = choosing ? chosen : state.prior.word;

  async function submitReopen() {
    const why = reason.trim();
    if (!why || (choosing && !chosen)) return;
    setBusy(true);
    try {
      const res = await reopenDeal(dealId, why, choosing ? chosen : undefined);
      if (!res || res.ok === false) throw new Error(res?.message || res?.error || 'failed');
      toast.success(`Reopened: ${res.from} to ${res.to}`);
      setOpen(false);
      setState({ ...state, ended: false, word: res.to, prior: null, may_choose: false });
      if (typeof onReopened === 'function') onReopened(res);
    } catch (err) {
      toast.error(`Not reopened: ${err?.body?.message || err?.message || 'failed'}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button type="button" variant="ghost" size="xs" className="text-text-dim hover:text-text" data-deal-reopen="offered"
        title="Edge case: undo an ended move made in error" onClick={() => setOpen(true)}>
        Reopen
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reopen this ended deal?</DialogTitle>
            <DialogDescription>
              {choosing ? (
                <>TRACON does not know the stage this deal held before it was marked <strong className="text-text">{state.word}</strong>,
                so choose the stage to put back. It is recorded as chosen by you.</>
              ) : (
                <>This puts the deal back to <strong className="text-text">{prior}</strong>, the stage it held before it was
                marked <strong className="text-text">{state.word}</strong>.</>
              )}{' '}
              Use it only to undo an ended move made in error.
              A client who comes back is a new deal: start a renewal or a new deal from this policy instead.
            </DialogDescription>
          </DialogHeader>
          {choosing ? (
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Stage to put back (required)</span>
              <select
                required
                value={chosen}
                onChange={(e) => setChosen(e.target.value)}
                className="mt-1.5 flex h-9 w-full rounded-card border border-border bg-bg px-3 text-sm text-text focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="">Choose a stage</option>
                {state.choices.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </label>
          ) : null}
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Reason (required)</span>
            <textarea
              autoFocus
              required
              rows={2}
              value={reason}
              maxLength={REASON_MAX}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. marked Lost in error, the client paid"
              className={cn(
                'mt-1.5 flex w-full rounded-card border border-border bg-bg px-3 py-2 text-sm text-text',
                'placeholder:text-text-dim',
                'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 focus:ring-offset-bg',
                'resize-none'
              )}
            />
          </label>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button type="button" variant="outline" onClick={submitReopen} disabled={busy || !reason.trim() || (choosing && !chosen)}>
              {busy ? 'Reopening...' : (prior ? `Reopen to ${prior}` : 'Reopen')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
